import React from 'react';
import {
  Button,
  Input,
  Card,
  CardContent,
  Alert,
  AlertDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Search, Info } from 'lucide-react';
import { QA_STATUS_FILTER } from '../utils/qaStatusUtils';

export interface QASearchParams {
  patient_id: string;
  patient_first_name: string;
  patient_last_name: string;
  start_date: Date | null;
  end_date: Date | null;
  qa_status: string | null;
}

interface QASearchFormProps {
  searchParams: QASearchParams;
  onUpdateSearchParam: (
    key: keyof QASearchParams,
    value: string | Date | null
  ) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  searching: boolean;
  validationError?: string;
  onCloseValidationError: () => void;
}

const QASearchForm: React.FC<QASearchFormProps> = ({
  searchParams,
  onUpdateSearchParam,
  onSearch,
  onClearFilters,
  searching,
  validationError,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !searching) {
      onSearch();
    }
  };

  return (
    <Card className='mb-6'>
      <CardContent>
        {/* Search Fields - Row 1 */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4 items-end'>
          <div className='space-y-2'>
            <label className='text-sm font-medium text-gray-700'>
              Patient ID / MRN
            </label>
            <Input
              placeholder='Enter MRN'
              value={searchParams.patient_id}
              onChange={e =>
                onUpdateSearchParam(
                  'patient_id',
                  (e.target as HTMLInputElement).value
                )
              }
              disabled={searching}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium text-gray-700'>
              First Name
            </label>
            <Input
              placeholder='Enter first name'
              value={searchParams.patient_first_name}
              onChange={e =>
                onUpdateSearchParam(
                  'patient_first_name',
                  (e.target as HTMLInputElement).value
                )
              }
              disabled={searching}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium text-gray-700'>
              Last Name
            </label>
            <Input
              placeholder='Enter last name'
              value={searchParams.patient_last_name}
              onChange={e =>
                onUpdateSearchParam(
                  'patient_last_name',
                  (e.target as HTMLInputElement).value
                )
              }
              disabled={searching}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium text-gray-700'>
              QA Status
            </label>
            <Select
              value={searchParams.qa_status || ''}
              onValueChange={value =>
                onUpdateSearchParam('qa_status', value || null)
              }
              disabled={searching}
            >
              <SelectTrigger>
                <SelectValue placeholder='All Statuses' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={QA_STATUS_FILTER.NOT_STARTED}>
                  Not Started
                </SelectItem>
                <SelectItem value={QA_STATUS_FILTER.IN_PROGRESS}>
                  In Progress
                </SelectItem>
                <SelectItem value={QA_STATUS_FILTER.COMPLETED}>
                  Completed
                </SelectItem>
                <SelectItem value={QA_STATUS_FILTER.CANCELLED_FAILED}>
                  Cancelled/Failed
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Search Fields - Row 2 */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4 items-end mt-4'>
          <div className='space-y-2'>
            <label className='text-sm font-medium text-gray-700'>
              Start Date
            </label>
            <Input
              type='date'
              value={
                searchParams.start_date
                  ? searchParams.start_date.toISOString().split('T')[0]
                  : ''
              }
              onChange={e =>
                onUpdateSearchParam(
                  'start_date',
                  (e.target as HTMLInputElement).value
                    ? new Date((e.target as HTMLInputElement).value)
                    : null
                )
              }
              disabled={searching}
            />
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium text-gray-700'>
              End Date
            </label>
            <Input
              type='date'
              value={
                searchParams.end_date
                  ? searchParams.end_date.toISOString().split('T')[0]
                  : ''
              }
              onChange={e =>
                onUpdateSearchParam(
                  'end_date',
                  (e.target as HTMLInputElement).value
                    ? new Date((e.target as HTMLInputElement).value)
                    : null
                )
              }
              disabled={searching}
            />
          </div>

          <div className='md:col-span-2 flex gap-2 justify-end'>
            <Button onClick={onSearch} disabled={searching}>
              <Search className='w-4 h-4' />
              Search
            </Button>
            <Button
              variant='outline'
              onClick={onClearFilters}
              disabled={searching}
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Validation Error Alert */}
        {validationError && (
          <Alert className='border-red-200 bg-red-50'>
            <Info className='h-4 w-4 text-red-600' />
            <AlertDescription className='text-red-800'>
              {validationError}
            </AlertDescription>
          </Alert>
        )}

        {/* Help Text */}
        <div className='flex items-center gap-2 text-xs text-gray-500 mt-4'>
          <Info className='w-3 h-3' />
          <span>
            Enter search criteria and click Search. Date range defaults to last
            6 weeks if not specified.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default QASearchForm;
